import Swiper from 'swiper'
import { prepareSwiperDom, resolveSliderRoot } from './prepareDom'
import { getSwiperOptions } from './getSwiperOptions'
import { initPaginationVisibility, onActiveBulletChange } from './paginationVisibility'
import { prepareLazyImagePlaceholders, hydrateVisibleSlideImages } from './lazyImages'

import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/grid'
import './adagio-swiper.scss'

const instances = new WeakMap()

/** Toggle .swiper-inactive when watchOverflow locks the slider. */
export function syncInactiveClass(swiper) {
  if (!swiper?.el) return
  swiper.el.classList.toggle('swiper-inactive', Boolean(swiper.isLocked))
}

/**
 * Initialize Swiper on a legacy widget root.
 *
 * @param {HTMLElement} root - Widget wrapper or slide container
 * @param {object} config
 * @param {string} config.preset - Preset key (threeCol, quotes, …)
 * @param {string} [config.containerSelector='.slider-container']
 * @param {string} [config.paginationSelector='.adagio-swiper-pagination']
 * @param {string} [config.theme='black'] - Swiper color theme (black | white)
 * @param {object} [config.overrides] - Swiper option overrides
 * @param {boolean} [config.skipNavigation=false] - Skip external nav wrapper
 * @param {boolean} [config.skipPagination=false] - Skip pagination wrapper
 * @param {function} [config.onInit] - Called after init (e.g. matchHeight)
 */
export function initSwiper(root, {
  preset = 'default',
  containerSelector = '.slider-container',
  paginationSelector = '.adagio-swiper-pagination',
  navigationSelector = '.adagio-swiper-navigation',
  theme = 'black',
  skipNavigation = false,
  skipPagination = false,
  overrides = {},
  onInit = null,
} = {}) {
  const container = resolveSliderRoot(root, containerSelector)
  if (!container) {
    console.warn('[adagio-swiper] Container not found:', containerSelector, root)
    return null
  }

  const existing = instances.get(container) || container.swiper
  if (existing) {
    return existing
  }

  const paginationContainer = skipPagination
    ? null
    : container.nextElementSibling?.matches?.(paginationSelector)
      ? container.nextElementSibling
      : root.querySelector?.(paginationSelector)
        ?? container.parentElement?.querySelector?.(paginationSelector)

  const {
    container: swiperEl,
    paginationEl,
    prevElements,
    nextElements,
  } = prepareSwiperDom(container, {
    paginationContainer,
    navigationSelector,
    theme,
    skipNavigation,
    skipPagination,
  })

  const userOn = overrides.on || {}
  const options = getSwiperOptions({
    preset,
    overrides: {
      ...overrides,
      on: undefined,
    },
    navigationElements: skipNavigation ? {} : { prevEl: prevElements, nextEl: nextElements },
    paginationElement: paginationEl,
  })

  options.watchSlidesProgress = true

  options.on = {
    ...userOn,
    init(swiper) {
      syncInactiveClass(swiper)
      initPaginationVisibility(swiper)
      prepareLazyImagePlaceholders(swiper)
      hydrateVisibleSlideImages(swiper)
      userOn.init?.(swiper)
      onInit?.(swiper)
    },
    resize(swiper) {
      syncInactiveClass(swiper)
      initPaginationVisibility(swiper)
      hydrateVisibleSlideImages(swiper)
      userOn.resize?.(swiper)
    },
    breakpoint(swiper) {
      syncInactiveClass(swiper)
      initPaginationVisibility(swiper)
      hydrateVisibleSlideImages(swiper)
      userOn.breakpoint?.(swiper)
    },
    lock(swiper) {
      syncInactiveClass(swiper)
      userOn.lock?.(swiper)
    },
    unlock(swiper) {
      syncInactiveClass(swiper)
      userOn.unlock?.(swiper)
    },
    slideChange(swiper) {
      onActiveBulletChange(swiper)
      hydrateVisibleSlideImages(swiper)
      userOn.slideChange?.(swiper)
    },
  }

  const swiper = new Swiper(swiperEl, options)
  instances.set(container, swiper)

  return swiper
}

export function destroySwiper(root, containerSelector = '.slider-container') {
  const container = resolveSliderRoot(root, containerSelector)
  const swiper = container?.swiper || instances.get(container)
  if (swiper) {
    swiper.destroy(true, true)
    instances.delete(container)
  }
}
